import React from 'react';
import { SalesLayout } from '@/components/SalesLayout';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Pagination } from '@/components/Pagination';
import { Settings, ChevronDown, ChevronUp, Download, Mail, FileSpreadsheet, FileText, Search, X } from 'lucide-react';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Get date helpers - Fixed for timezone issues
const getFirstDayOfMonth = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}-01`;
};

const getTodayDate = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Format date for display
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return `${String(date.getDate()).padStart(2, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${date.getFullYear()}`;
  } catch {
    return dateStr;
  }
};

// Format currency
const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return '-';
  return `₹${Number(amount).toLocaleString('en-IN')}`;
};

// Exchange Vehicle Fields (editable)
const EXCHANGE_FIELDS = [
  { id: 'delivery_date', label: 'Date of Delivery', type: 'date' },
  { id: 'exchange_vehicle_no', label: 'Exchange Vehicle No', type: 'text' },
  { id: 'ex_brand_name', label: 'Exchange Brand', type: 'text' },
  { id: 'ex_model_name', label: 'Exchange Model', type: 'text' },
  { id: 'ex_variant_name', label: 'Exchange Variant', type: 'text' },
  { id: 'ex_color', label: 'Exchange Color', type: 'text' },
  { id: 'year_of_manufacturing', label: 'Year of Manufacturing', type: 'text' },
  { id: 'exchange_value', label: 'Exchange Value (₹)', type: 'number' },
  { id: 'inwards_status', label: 'Inwards Status', type: 'select', options: ['Inwards Pending', 'Inwards Done', 'Delivered'] },
  { id: 'traffic_challans_pending', label: 'Traffic Challans Pending', type: 'number' },
];

// Customer & Booking Info Fields (display only)
const BOOKING_INFO_FIELDS = [
  { id: 'customer_first_name', label: 'Customer Name', computed: (b) => `${b.customer_first_name || ''} ${b.customer_last_name || ''}`.trim() },
  { id: 'mobile_no_1', label: 'Mobile No' },
  { id: 'email', label: 'Email' },
  { id: 'address_line_1', label: 'Address', computed: (b) => `${b.address_line_1 || ''} ${b.address_line_2 || ''} ${b.city || ''}`.trim() },
  { id: 'brand_name', label: 'New Vehicle Brand' },
  { id: 'model', label: 'New Vehicle Model' },
  { id: 'variant', label: 'New Vehicle Variant' },
  { id: 'color', label: 'New Vehicle Color' },
  { id: 'chassis_no', label: 'Chassis No' },
  { id: 'registration_number', label: 'Registration No' },
  { id: 'date_of_booking', label: 'Booking Date', format: 'date' },
  { id: 'delivery_date', label: 'Delivery Date', format: 'date' },
  { id: 'sales_representative', label: 'Sales Representative' },
  { id: 'dms_invoice_no', label: 'DMS Invoice No' },
  { id: 'dms_invoice_date', label: 'DMS Invoice Date', format: 'date' },
];

function ExchangeVehicleReport({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  
  // State
  const [bookings, setBookings] = React.useState([]);
  const [filteredData, setFilteredData] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [dateFrom, setDateFrom] = React.useState(getFirstDayOfMonth());
  const [dateTo, setDateTo] = React.useState(getTodayDate());
  const [currentPage, setCurrentPage] = React.useState(1);
  const [showExportModal, setShowExportModal] = React.useState(false);
  const [shareEmail, setShareEmail] = React.useState('');
  const [sendingEmail, setSendingEmail] = React.useState(false);
  
  // Search filters
  const [searchBookingNo, setSearchBookingNo] = React.useState('');
  const [searchCustomerName, setSearchCustomerName] = React.useState('');
  const [searchRegNo, setSearchRegNo] = React.useState('');
  const [searchExchangeVehicleNo, setSearchExchangeVehicleNo] = React.useState('');
  
  // Inline edit state
  const [editingCell, setEditingCell] = React.useState(null);
  const [editValue, setEditValue] = React.useState('');
  
  const itemsPerPage = 10;

  // Get auth header
  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  // Fetch bookings
  React.useEffect(() => {
    fetchBookings();
  }, []);

  // Apply filter when dates, bookings, or search terms change
  React.useEffect(() => {
    applyFilter();
  }, [bookings, dateFrom, dateTo, searchBookingNo, searchCustomerName, searchRegNo, searchExchangeVehicleNo]);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/bookings?limit=10000`, getAuthHeader());
      setBookings(response.data || []);
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
      toast.error('Failed to fetch bookings');
    } finally {
      setLoading(false);
    }
  };

  const applyFilter = () => {
    // Filter bookings that have exchange vehicle data and match delivery date range
    let filtered = bookings.filter(b => {
      // Must have exchange vehicle info
      const hasExchangeVehicle = b.exchange_vehicle_no || b.ex_brand_name || b.ex_model_name;
      if (!hasExchangeVehicle) return false;
      
      // Filter by delivery date
      if (!b.delivery_date) return false;
      
      const deliveryDate = new Date(b.delivery_date);
      const from = new Date(dateFrom);
      const to = new Date(dateTo);
      to.setHours(23, 59, 59);
      
      if (!(deliveryDate >= from && deliveryDate <= to)) return false;
      
      // Search filters
      // 1. Booking No search
      if (searchBookingNo) {
        const bookingNo = (b.booking_no || '').toString().toLowerCase();
        if (!bookingNo.includes(searchBookingNo.toLowerCase())) return false;
      }
      
      // 2. Customer Name search
      if (searchCustomerName) {
        const customerName = `${b.customer_first_name || ''} ${b.customer_last_name || ''}`.toLowerCase();
        if (!customerName.includes(searchCustomerName.toLowerCase())) return false;
      }
      
      // 3. Registration Number search
      if (searchRegNo) {
        const regNo = (b.registration_number || '').toLowerCase();
        if (!regNo.includes(searchRegNo.toLowerCase())) return false;
      }
      
      // 4. Exchange Vehicle Number search
      if (searchExchangeVehicleNo) {
        const exchangeVehicleNo = (b.exchange_vehicle_no || '').toLowerCase();
        if (!exchangeVehicleNo.includes(searchExchangeVehicleNo.toLowerCase())) return false;
      }
      
      return true;
    });
    
    // Sort by delivery date descending (most recent first)
    filtered.sort((a, b) => new Date(b.delivery_date) - new Date(a.delivery_date));
    
    setFilteredData(filtered);
    setCurrentPage(1);
  };
  
  // Clear all search filters
  const clearSearchFilters = () => {
    setSearchBookingNo('');
    setSearchCustomerName('');
    setSearchRegNo('');
    setSearchExchangeVehicleNo('');
  };

  // Pagination
  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Inline edit handlers
  const startEdit = (bookingId, fieldId, currentValue) => {
    setEditingCell({ bookingId, fieldId });
    setEditValue(currentValue || '');
  };

  const cancelEdit = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const saveEdit = async () => {
    if (!editingCell) return;
    
    try {
      const { bookingId, fieldId } = editingCell;
      const updateData = { [fieldId]: editValue };
      
      await axios.put(`${BACKEND_URL}/api/bookings/${bookingId}`, updateData, getAuthHeader());
      
      // Update local state
      setBookings(prev => prev.map(b => 
        b.id === bookingId ? { ...b, [fieldId]: editValue } : b
      ));
      
      toast.success('Updated successfully');
      cancelEdit();
    } catch (error) {
      console.error('Failed to update:', error);
      let errorMessage = 'Unknown error';
      if (error.response?.data?.detail) {
        errorMessage = typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : JSON.stringify(error.response.data.detail);
      } else if (error.message) {
        errorMessage = typeof error.message === 'string' 
          ? error.message 
          : JSON.stringify(error.message);
      }
      toast.error('Failed to update: ' + errorMessage);
    }
  };

  // Get field value for display
  const getFieldValue = (booking, field) => {
    if (field.computed) {
      return field.computed(booking);
    }
    const value = booking[field.id];
    if (field.format === 'date') {
      return formatDate(value);
    }
    if (field.type === 'number' && field.id.includes('value')) {
      return formatCurrency(value);
    }
    return value || '-';
  };

  // Render editable cell
  const renderEditableCell = (booking, field) => {
    const isEditing = editingCell?.bookingId === booking.id && editingCell?.fieldId === field.id;
    const currentValue = booking[field.id] || '';

    if (isEditing) {
      if (field.type === 'select') {
        return (
          <div className="flex gap-1 items-center">
            <select
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              className="p-1 border rounded text-xs w-full"
              autoFocus
            >
              <option value="">-- Select --</option>
              {field.options.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
            <button onClick={saveEdit} className="text-green-600 text-xs px-1 font-bold">✓</button>
            <button onClick={cancelEdit} className="text-red-600 text-xs px-1 font-bold">✗</button>
          </div>
        );
      } else if (field.type === 'date') {
        return (
          <div className="flex gap-1 items-center">
            <input
              type="date"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              className="p-1 border rounded text-xs w-full"
              autoFocus
            />
            <button onClick={saveEdit} className="text-green-600 text-xs px-1 font-bold">✓</button>
            <button onClick={cancelEdit} className="text-red-600 text-xs px-1 font-bold">✗</button>
          </div>
        );
      } else {
        return (
          <div className="flex gap-1 items-center">
            <input
              type={field.type === 'number' ? 'number' : 'text'}
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              className="p-1 border rounded text-xs w-full"
              autoFocus
            />
            <button onClick={saveEdit} className="text-green-600 text-xs px-1 font-bold">✓</button>
            <button onClick={cancelEdit} className="text-red-600 text-xs px-1 font-bold">✗</button>
          </div>
        );
      }
    }

    // Display value
    let displayValue = currentValue || '-';
    if (field.type === 'date' && currentValue) {
      displayValue = formatDate(currentValue);
    } else if (field.type === 'number' && field.id.includes('value') && currentValue) {
      displayValue = formatCurrency(currentValue);
    }

    // Status badge for inwards_status
    const statusClass = field.id === 'inwards_status' && currentValue
      ? currentValue === 'Inwards Done' || currentValue === 'Delivered'
        ? 'bg-green-100 text-green-800'
        : 'bg-yellow-100 text-yellow-800'
      : '';

    return (
      <span 
        className={`cursor-pointer hover:bg-teal-50 px-2 py-1 rounded inline-block text-xs ${statusClass}`}
        onClick={() => startEdit(booking.id, field.id, currentValue)}
        title="Click to edit"
      >
        {displayValue}
      </span>
    );
  };

  // Handle navigation to full edit with return URL
  const handleFullEdit = (bookingId) => {
    const returnUrl = encodeURIComponent(location.pathname + location.search);
    navigate(`/sales/bookings/edit/${bookingId}?returnTo=${returnUrl}`);
  };

  // Export to Excel
  const handleExportExcel = () => {
    const exportData = filteredData.map((b, idx) => {
      const row = { 'S.No': idx + 1, 'Booking No': b.booking_no };
      
      // Add booking info fields
      BOOKING_INFO_FIELDS.forEach(field => {
        row[field.label] = getFieldValue(b, field);
      });
      
      // Add exchange fields
      EXCHANGE_FIELDS.forEach(field => {
        row[field.label] = field.type === 'number' && field.id.includes('value')
          ? b[field.id] || 0
          : b[field.id] || '-';
      });
      
      return row;
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Exchange Vehicles');
    XLSX.writeFile(wb, `Exchange_Vehicle_Report_${dateFrom}_to_${dateTo}.xlsx`);
    toast.success('Excel exported successfully!');
    setShowExportModal(false);
  };

  // Export to PDF
  const handleExportPDF = () => {
    const doc = new jsPDF('l', 'mm', 'a4');
    
    doc.setFontSize(16);
    doc.text('Exchange Vehicle Report', 14, 15);
    doc.setFontSize(10);
    doc.text(`Date Range: ${formatDate(dateFrom)} to ${formatDate(dateTo)}`, 14, 22);
    doc.text(`Total Records: ${filteredData.length}`, 14, 28);

    const headers = ['S.No', 'B.No', 'Customer', 'Exchange Vehicle', 'Exchange Value', 'Inwards Status', 'Delivery Date'];
    const data = filteredData.map((b, idx) => [
      idx + 1,
      b.booking_no,
      `${b.customer_first_name || ''} ${b.customer_last_name || ''}`.trim(),
      `${b.ex_brand_name || ''} ${b.ex_model_name || ''} (${b.exchange_vehicle_no || '-'})`,
      formatCurrency(b.exchange_value),
      b.inwards_status || '-',
      formatDate(b.delivery_date)
    ]);

    doc.autoTable({
      head: [headers],
      body: data,
      startY: 35,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [20, 184, 166] }
    });

    doc.save(`Exchange_Vehicle_Report_${dateFrom}_to_${dateTo}.pdf`);
    toast.success('PDF exported successfully!');
    setShowExportModal(false);
  };

  // Send via Email
  const handleSendEmail = async () => {
    if (!shareEmail) {
      toast.error('Please enter an email address');
      return;
    }

    try {
      setSendingEmail(true);
      
      // Generate summary for email
      const summary = filteredData.map((b, idx) => 
        `${idx + 1}. B.No: ${b.booking_no} | ${b.customer_first_name} ${b.customer_last_name || ''} | Exchange: ${b.ex_brand_name || ''} ${b.ex_model_name || ''} (${b.exchange_vehicle_no || '-'}) | Value: ${formatCurrency(b.exchange_value)} | Status: ${b.inwards_status || '-'}`
      ).join('\n');

      await axios.post(`${BACKEND_URL}/api/reports/email`, {
        to_email: shareEmail,
        subject: `Exchange Vehicle Report (${formatDate(dateFrom)} - ${formatDate(dateTo)})`,
        body: `Exchange Vehicle Report\n\nDate Range: ${formatDate(dateFrom)} to ${formatDate(dateTo)}\nTotal Records: ${filteredData.length}\n\n${summary}`
      }, getAuthHeader());

      toast.success('Email sent successfully!');
      setShowExportModal(false);
      setShareEmail('');
    } catch (error) {
      toast.error('Failed to send email: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSendingEmail(false);
    }
  };

  // Summary stats
  const totalExchangeValue = filteredData.reduce((sum, b) => sum + (Number(b.exchange_value) || 0), 0);
  const inwardsDone = filteredData.filter(b => b.inwards_status === 'Inwards Done' || b.inwards_status === 'Delivered').length;
  const inwardsPending = filteredData.filter(b => b.inwards_status === 'Inwards Pending').length;

  if (loading) {
    return (
      <SalesLayout user={user} onLogout={onLogout} title="Exchange Vehicle Report">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
        </div>
      </SalesLayout>
    );
  }

  return (
    <SalesLayout user={user} onLogout={onLogout} title="Exchange Vehicle Report">
      <div className="space-y-4">
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-500 to-emerald-600 rounded-xl p-6 text-white">
          <h1 className="text-2xl font-bold">Exchange Vehicle Report</h1>
          <p className="text-teal-100 mt-1">Track and manage exchange vehicles based on delivery date</p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl p-4 shadow-sm border space-y-4">
          {/* Date Filters Row */}
          <div className="flex flex-wrap gap-4 items-end">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Delivery Date From</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="p-2 border rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Delivery Date To</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="p-2 border rounded-lg text-sm"
              />
            </div>
            <button
              onClick={() => { setDateFrom(getFirstDayOfMonth()); setDateTo(getTodayDate()); }}
              className="px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 text-sm font-medium"
            >
              This Month
            </button>
            <div className="flex-1" />
            <button
              onClick={() => setShowExportModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
            >
              <Download className="w-4 h-4" /> Export / Email
            </button>
          </div>
          
          {/* Search Filters Row */}
          <div className="border-t pt-4">
            <div className="flex items-center gap-2 mb-3">
              <Search className="w-4 h-4 text-teal-600" />
              <span className="text-sm font-medium text-gray-700">Search Filters</span>
              {(searchBookingNo || searchCustomerName || searchRegNo || searchExchangeVehicleNo) && (
                <button
                  onClick={clearSearchFilters}
                  className="ml-2 flex items-center gap-1 px-2 py-1 bg-red-100 text-red-600 rounded-md text-xs hover:bg-red-200"
                >
                  <X className="w-3 h-3" /> Clear All
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Booking No</label>
                <input
                  type="text"
                  value={searchBookingNo}
                  onChange={(e) => setSearchBookingNo(e.target.value)}
                  placeholder="Search booking..."
                  className="w-full p-2 border rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Customer Name</label>
                <input
                  type="text"
                  value={searchCustomerName}
                  onChange={(e) => setSearchCustomerName(e.target.value)}
                  placeholder="Search customer..."
                  className="w-full p-2 border rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Registration No</label>
                <input
                  type="text"
                  value={searchRegNo}
                  onChange={(e) => setSearchRegNo(e.target.value)}
                  placeholder="Search reg no..."
                  className="w-full p-2 border rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Exchange Vehicle No</label>
                <input
                  type="text"
                  value={searchExchangeVehicleNo}
                  onChange={(e) => setSearchExchangeVehicleNo(e.target.value)}
                  placeholder="Search exchange no..."
                  className="w-full p-2 border rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-teal-50 rounded-xl p-4 text-center border border-teal-200">
            <div className="text-3xl font-bold text-teal-600">{filteredData.length}</div>
            <div className="text-sm text-teal-700">Total Exchange Vehicles</div>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 text-center border border-emerald-200">
            <div className="text-3xl font-bold text-emerald-600">{formatCurrency(totalExchangeValue)}</div>
            <div className="text-sm text-emerald-700">Total Exchange Value</div>
          </div>
          <div className="bg-green-50 rounded-xl p-4 text-center border border-green-200">
            <div className="text-3xl font-bold text-green-600">{inwardsDone}</div>
            <div className="text-sm text-green-700">Inwards Done/Delivered</div>
          </div>
          <div className="bg-yellow-50 rounded-xl p-4 text-center border border-yellow-200">
            <div className="text-3xl font-bold text-yellow-600">{inwardsPending}</div>
            <div className="text-sm text-yellow-700">Inwards Pending</div>
          </div>
        </div>

        {/* Pagination Header */}
        <div className="bg-white rounded-xl p-3 shadow-sm border flex items-center justify-between">
          <span className="text-sm text-gray-600">
            Showing {filteredData.length} exchange vehicles for delivery date range
          </span>
          <Pagination
            currentPage={currentPage}
            totalItems={filteredData.length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            showInfo={true}
          />
        </div>

        {/* 2-Row Card Layout */}
        <div className="space-y-3">
          {paginatedData.length === 0 ? (
            <div className="bg-white rounded-xl border shadow-sm p-8 text-center text-gray-500">
              No exchange vehicles found for the selected date range
            </div>
          ) : (
            paginatedData.map((booking, index) => {
              const rowNumber = (currentPage - 1) * itemsPerPage + index + 1;
              
              return (
                <div 
                  key={booking.id}
                  className="bg-white rounded-xl border shadow-sm overflow-hidden"
                >
                  {/* Row 1: Header */}
                  <div className="bg-gradient-to-r from-teal-50 to-emerald-50 p-3 border-b flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <span className="bg-teal-600 text-white text-xs font-bold px-2 py-1 rounded">
                        #{rowNumber}
                      </span>
                      <span className="font-bold text-teal-700 text-lg">{booking.booking_no}</span>
                      <span className="text-sm font-medium text-gray-800">
                        {booking.customer_first_name} {booking.customer_last_name || ''}
                      </span>
                      <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded border">
                        Delivery: {formatDate(booking.delivery_date)}
                      </span>
                    </div>
                    <button
                      onClick={() => handleFullEdit(booking.id)}
                      className="px-4 py-1.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 text-sm font-medium transition-colors"
                    >
                      Full Edit
                    </button>
                  </div>

                  {/* Row 2: Two-Column Layout */}
                  <div className="p-4">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Left Column: Booking Info (Display Only) */}
                      <div>
                        <h5 className="text-xs font-semibold text-gray-500 uppercase mb-3 flex items-center gap-2">
                          <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                          Booking & Customer Info
                        </h5>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                          {BOOKING_INFO_FIELDS.map(field => (
                            <div key={field.id} className="flex flex-col">
                              <span className="text-[10px] text-gray-400 uppercase">{field.label}</span>
                              <span className="text-sm font-medium text-gray-800 truncate" title={getFieldValue(booking, field)}>
                                {getFieldValue(booking, field)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Right Column: Exchange Fields (Editable) */}
                      <div>
                        <h5 className="text-xs font-semibold text-teal-600 uppercase mb-3 flex items-center gap-2">
                          <span className="w-2 h-2 bg-teal-500 rounded-full"></span>
                          Exchange Vehicle Details (Click to Edit)
                        </h5>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                          {EXCHANGE_FIELDS.map(field => (
                            <div key={field.id} className="flex flex-col">
                              <span className="text-[10px] text-gray-400 uppercase">{field.label}</span>
                              <div className="bg-teal-50/50 rounded p-1">
                                {renderEditableCell(booking, field)}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Bottom Pagination */}
        {filteredData.length > itemsPerPage && (
          <div className="bg-white rounded-xl border shadow-sm p-3 flex justify-end">
            <Pagination
              currentPage={currentPage}
              totalItems={filteredData.length}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              showInfo={true}
            />
          </div>
        )}

        {/* Export Modal */}
        {showExportModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-2xl">
              <h3 className="text-lg font-bold mb-4">Export & Share Report</h3>
              
              <div className="space-y-4">
                {/* Export Buttons */}
                <div className="grid grid-cols-2 gap-3">
                  <button 
                    onClick={handleExportExcel}
                    className="flex items-center justify-center gap-2 p-4 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
                  >
                    <FileSpreadsheet className="w-5 h-5" />
                    Export Excel
                  </button>
                  <button 
                    onClick={handleExportPDF}
                    className="flex items-center justify-center gap-2 p-4 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
                  >
                    <FileText className="w-5 h-5" />
                    Export PDF
                  </button>
                </div>

                {/* Email Section */}
                <div className="border-t pt-4">
                  <label className="block text-sm font-medium mb-2">Send via Email</label>
                  <div className="flex gap-2">
                    <input
                      type="email"
                      value={shareEmail}
                      onChange={(e) => setShareEmail(e.target.value)}
                      placeholder="Enter email address"
                      className="flex-1 p-2 border rounded-lg text-sm"
                    />
                    <button
                      onClick={handleSendEmail}
                      disabled={sendingEmail}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                      <Mail className="w-4 h-4" />
                      {sendingEmail ? 'Sending...' : 'Send'}
                    </button>
                  </div>
                </div>

                {/* Close Button */}
                <button
                  onClick={() => { setShowExportModal(false); setShareEmail(''); }}
                  className="w-full py-2 border border-gray-300 rounded-lg hover:bg-gray-100 text-gray-600"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </SalesLayout>
  );
}

export default ExchangeVehicleReport;
